"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiQuotesFill, PiPlus, PiTrash, PiUserCircle, PiCaretLeft, PiCaretRight } from "react-icons/pi";
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

export default function TestimonialsCarousel({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const testimonials = content.testimonials || [
        { name: "John Deo", role: "Ui Designer", text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam." },
        { name: "John Deo", role: "Ui Designer", text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam." },
        { name: "John Deo", role: "Ui Designer", text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam." },
    ];

    const addTestimonial = () => {
        const newItems = [...testimonials, { name: "New Client", role: "Role", text: "New testimonial text..." }];
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const removeTestimonial = (idx: number) => {
        const newItems = testimonials.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const updateTestimonial = (idx: number, field: string, value: string) => {
        const newItems = [...testimonials];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onContentChange?.({ ...content, testimonials: newItems });
    };

    return (
        <div
            className={`relative overflow-hidden py-24 ${isSelected ? "ring-2 ring-blue-500 z-10" : ""}`}
            style={{
                fontFamily,
                backgroundColor: '#2D1B69', // Deep Purple Background
            }}
        >
            {/* Abstract Background Blobs */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-orange-500 rounded-full mix-blend-screen filter blur-[100px] opacity-20"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-purple-600 rounded-full mix-blend-screen filter blur-[100px] opacity-30"></div>
                <div className="absolute top-[40%] left-[60%] w-[300px] h-[300px] bg-pink-500 rounded-full mix-blend-screen filter blur-[80px] opacity-20"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4">
                {/* Section Title */}
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
                        <EditableText
                            value={content.title || "What Our Customers Says?"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                    <div className="w-24 h-1 bg-orange-500 mx-auto rounded-full mt-4"></div>
                </div>

                {/* Swiper Carousel */}
                <div className="testimonial-carousel-container relative max-w-6xl mx-auto">
                    <Swiper
                        effect={'coverflow'}
                        grabCursor={true}
                        centeredSlides={true}
                        slidesPerView={'auto'}
                        initialSlide={1}
                        coverflowEffect={{
                            rotate: 0,
                            stretch: 0,
                            depth: 100,
                            modifier: 2.5,
                            slideShadows: true,
                        }}
                        pagination={{ clickable: true, dynamicBullets: true }}
                        modules={[EffectCoverflow, Pagination, Navigation]}
                        className="mySwiper !pb-12"
                        breakpoints={{
                            320: {
                                slidesPerView: 1,
                                spaceBetween: 20
                            },
                            768: {
                                slidesPerView: 2,
                                spaceBetween: 30
                            },
                            1024: {
                                slidesPerView: 3,
                                spaceBetween: 40
                            }
                        }}
                    >
                        {testimonials.map((item: any, idx: number) => (
                            <SwiperSlide key={idx} className="!w-[350px] md:!w-[400px]">
                                <div className="bg-white rounded-[40px] p-8 shadow-2xl h-full flex flex-col items-center text-center relative mx-4 my-4">

                                    {isSelected && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeTestimonial(idx); }}
                                            className="absolute top-4 right-4 z-50 text-gray-500 hover:text-red-500 bg-gray-100 rounded-full p-2"
                                            title="Remove"
                                        >
                                            <PiTrash />
                                        </button>
                                    )}

                                    {/* Avatar */}
                                    <div className="w-24 h-24 rounded-full border-4 border-white shadow-lg overflow-hidden -mt-16 mb-6 bg-gray-200">
                                        {item.image || item.avatar ? (
                                            <img src={item.image || item.avatar} alt={item.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <PiUserCircle className="w-full h-full text-gray-400" />
                                        )}
                                    </div>

                                    {/* Name & Role */}
                                    <h3 className="text-xl font-bold text-gray-800 mb-1">
                                        <EditableText
                                            value={item.name}
                                            onChange={(val) => updateTestimonial(idx, "name", val)}
                                            as="span"
                                            placeholder="Name"
                                        />
                                    </h3>
                                    <p className="text-orange-500 font-medium text-sm uppercase tracking-wider mb-6">
                                        <EditableText
                                            value={item.role}
                                            onChange={(val) => updateTestimonial(idx, "role", val)}
                                            as="span"
                                            placeholder="Role"
                                        />
                                    </p>

                                    {/* Text */}
                                    <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                                        <EditableText
                                            value={item.text}
                                            onChange={(val) => updateTestimonial(idx, "text", val)}
                                            as="span"
                                            placeholder="Testimonial text..."
                                        />
                                    </p>

                                    {/* Quote Icon */}
                                    <div className="mt-auto text-orange-200">
                                        <PiQuotesFill className="w-10 h-10" />
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {isSelected && (
                        <div className="text-center mt-4">
                            <button
                                onClick={addTestimonial}
                                className="inline-flex items-center gap-2 px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors border border-white/20"
                            >
                                <PiPlus /> Add Slide
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style jsx global>{`
                .swiper-pagination-bullet {
                    background: #fff !important;
                    opacity: 0.5;
                }
                .swiper-pagination-bullet-active {
                    background: #f97316 !important; /* Orange-500 */
                    opacity: 1;
                }
            `}</style>
        </div>
    );
}
